"use client";

import { useState } from "react";
import { Mail, CheckCircle, AlertCircle, ArrowLeft, Info } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Icons } from "@/components/ui/icons";

interface EmailVerificationProps {
  email: string;
  onCancel: () => void;
}

export function EmailVerification({
  email,
  onCancel,
}: EmailVerificationProps) {
  const [isResending, setIsResending] = useState(false);
  const [message, setMessage] = useState<string>("");
  const [messageType, setMessageType] = useState<"success" | "error" | "info">("info");

  const handleResend = async () => {
    setIsResending(true);
    setMessage("Sending verification email...");
    setMessageType("info");

    try {
      // Canonical resend endpoint lives at /api/auth/resend-verification; the
      // older /api/auth/verify-email/resend path was never implemented.
      const response = await fetch("/api/auth/resend-verification", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage("Verification email resent successfully!");
        setMessageType("success");
      } else {
        setMessage(data.message || "Failed to resend verification email");
        setMessageType("error");
      }
    } catch (error) {
      setMessage("Failed to resend verification email");
      setMessageType("error");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f3f4f6] dark:bg-background">
      <Card className="w-full max-w-md mx-auto border-0 shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto w-14 h-14 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="w-7 h-7 text-green-600" />
          </div>
          <CardTitle className="text-2xl font-bold">Check Your Email</CardTitle>
          <CardDescription className="text-center">
            We've sent a verification link to<br />
            <span className="font-medium text-primary block mt-1">{email}</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ul className="text-sm text-muted-foreground space-y-1 pl-4 list-disc">
            <li>Click the link in the email to verify your account</li>
            <li>If you don't see the email, check your spam folder</li>
          </ul>
          <div className="flex items-center gap-2 bg-muted/60 border border-muted-200 rounded-md px-3 py-2 text-xs text-muted-foreground">
            <Info className="h-4 w-4 text-muted-foreground" />
            If an account with that email exists, we've sent you a verification link.
          </div>
          {message && (
            <Alert
              className={
                messageType === "error"
                  ? "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950"
                  : messageType === "success"
                    ? "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950"
                    : "border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950"
              }
            >
              {messageType === "error" ? (
                <AlertCircle className="h-4 w-4" />
              ) : messageType === "success" ? (
                <CheckCircle className="h-4 w-4" />
              ) : (
                <Mail className="h-4 w-4" />
              )}
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}
        </CardContent>
        <CardFooter className="flex flex-col gap-2">
            <Button
            variant="outline"
              className="w-full"
            onClick={handleResend}
            disabled={isResending}
            >
            {isResending && (
              <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
            )}
            Resend verification email
            </Button>
              <Button
            variant="ghost"
            className="w-full"
            onClick={onCancel}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to registration
              </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
